import React, { useRef, useState, useCallback } from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import { Modal, Input, Button, Spin, Radio, Select, Form } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import numberToWords from 'number-to-words'
import usFlag from '@assets/images/common/flags/us.png'
import euFlag from '@assets/images/common/flags/eu.png'
import hippaFlag from '@assets/images/common/flags/hippa.png'
import customLanguageTexts from '../../../language'
import constants from '../../../constants'

const ModalNewApp = () => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const visibleModalID = useSelector(state => state.settings.visibleModalID)
  const apps = useSelector(state => state.apps)
  const modalID = 'newapp'
  const visible = modalID === visibleModalID

  const [timerLength] = useState(14)
  const [currentSecond, setCurrentSecond] = useState(0)
  const [appCreation, setAppCreation] = useState(false)
  const [newAppOtherTechnology, setNewAppOtherTechnology] = useState(false)
  const [newAppOtherUsecase, setNewAppOtherUsecase] = useState(false)

  const timerInterval = useRef(null)
  const timer = useRef(null)

  const handleTechnologyChange = values => {
    setNewAppOtherTechnology(_.includes(values, 'other'))
  }

  const handleUsecaseChange = value => {
    setNewAppOtherUsecase(value === 'other')
  }

  const handleCancel = useCallback(() => {
    dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: { setting: 'visibleModalID', value: '' },
    })
    clearInterval(timerInterval.current)
    clearTimeout(timer.current)
    setTimeout(() => {
      setCurrentSecond(0)
      setAppCreation(false)
    }, 500)
  }, [dispatch])

  const onSubmit = useCallback(() => {
    clearInterval(timerInterval.current)
    form.validateFields().then(values => {
      const payload = {
        name: values.name,
        region: values.region,
        version: values.version,
        metadata: {
          technologies: values.technology,
          technology_other: values.technology_other,
          useCase: values.usecase,
          useCase_other: values.usecase_other,
        },
      }
      dispatch({ type: 'apps/ADD', payload })

      if (['us', 'eu', 'in'].includes(values.region)) {
        setAppCreation(true)
        timerInterval.current = setInterval(() => {
          setCurrentSecond(prev => prev + 1)
        }, 1000)
        timer.current = setTimeout(() => {
          handleCancel()
        }, timerLength * 1000)
        if (constants.captureGTMEvents && window.CometChatAnalytics) {
          window.CometChatAnalytics.event(constants.GAEvents.app_creation)
        }
      }
    })
  }, [form, dispatch, timerLength, handleCancel])

  let newAppOtherTechnologyClasses = 'newAppFields newAppOtherTechnology hidden-ele'
  if (newAppOtherTechnology) {
    newAppOtherTechnologyClasses = 'newAppFields newAppOtherTechnology'
  }

  let newAppOtherUsecaseClasses = 'newAppFields newAppOtherUsecase hidden-ele'
  if (newAppOtherUsecase) {
    newAppOtherUsecaseClasses = 'newAppFields newAppOtherUsecase'
  }

  return (
    <Modal
      className="com__modal"
      title={<span className="font-size-24">Add New App</span>}
      open={visible}
      maskClosable={false}
      destroyOnHidden
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit} className="mt-3 mb-4">
        <div className="com__fields maxWidth52">
          <Form.Item
            label="Name"
            className="newAppFields newAppName"
            name="name"
            initialValue={`My ${numberToWords.toWordsOrdinal(apps.appsListTotal + 1)} app`}
            rules={[{ required: true, message: 'Please input app Name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Region"
            name="region"
            initialValue="us"
            rules={[{ required: true, message: 'Please select Region' }]}
          >
            <Radio.Group buttonStyle="solid" className="com__clusterIcons">
              <Radio.Button value="us" className="us-app-region">
                <img src={usFlag} alt="US" /> US
              </Radio.Button>
              <Radio.Button value="eu" className="eu-app-region">
                <img src={euFlag} alt="EU" /> EU
              </Radio.Button>
              <Radio.Button value="in" className="in-app-region">
                <span role="img" aria-label="IN">🇮🇳</span> IN
              </Radio.Button>
              <Radio.Button
                value="hipaa-us"
                className="hipaa-app-region"
                style={{ display: 'inline-block', paddingLeft: '1.42rem', paddingRight: '1.42rem' }}
              >
                <img
                  src={hippaFlag}
                  alt="US (HIPAA COMPLIANT)"
                  className="hippa-appcard-icon"
                />
                US (HIPAA COMPLIANT)
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <div className="app-region-info-text mb-4">
            {customLanguageTexts.ADD_NEW_APP_CARD_REGION_TEXT}
          </div>

          <Form.Item
            label="Version"
            name="version"
            initialValue="3"
            rules={[{ required: true, message: 'Please select Version' }]}
          >
            <Radio.Group className="app__version__field">
              <Radio value="2">v2</Radio>
              <Radio value="3">v3</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Technology" name="technology" initialValue={[]}>
            <Select
              mode="multiple"
              onChange={handleTechnologyChange}
              showSearch
              notFoundContent="Not found on our list!"
            >
              <Select.Option value="swift">Swift (iOS)</Select.Option>
              <Select.Option value="objective_c">Objective C (iOS)</Select.Option>
              <Select.Option value="kotlin">Kotlin (Android)</Select.Option>
              <Select.Option value="java">Java (Android)</Select.Option>
              <Select.Option value="react">React (Web)</Select.Option>
              <Select.Option value="angular">Angular (Web)</Select.Option>
              <Select.Option value="vue">Vue (Web)</Select.Option>
              <Select.Option value="jquery_bootstrap">jQuery/Bootstrap (Web)</Select.Option>
              <Select.Option value="react_native">React Native</Select.Option>
              <Select.Option value="ionic_cordova">Ionic/Cordova</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Others"
            name="technology_other"
            className={newAppOtherTechnologyClasses}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Use Case" name="usecase">
            <Select onChange={handleUsecaseChange}>
              <Select.Option value="">Choose your use case</Select.Option>
              <Select.Option value="marketplace">Marketplace</Select.Option>
              <Select.Option value="on_demand">On-demand</Select.Option>
              <Select.Option value="online_community">Online Community</Select.Option>
              <Select.Option value="healthcare">Healthcare</Select.Option>
              <Select.Option value="gaming">Gaming</Select.Option>
              <Select.Option value="live_streaming">Live Streaming</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Other use case"
            name="usecase_other"
            className={newAppOtherUsecaseClasses}
          >
            <Input />
          </Form.Item>
        </div>

        <div className="com__formLine" />
        <Button
          loading={apps.loading}
          type="primary"
          className="com__button mr-4"
          htmlType="submit"
        >
          Add App
        </Button>
        <a role="button" tabIndex="-1" onKeyPress={handleCancel} onClick={handleCancel}>
          Close
        </a>
      </Form>

      {appCreation && (
        <div className="com__appLoading">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <p>
            <span className="font-size-21">We are configuring your app.</span>
            <br />
            <span className="font-size-18">
              Please allow us <strong>{timerLength - currentSecond}</strong> seconds.
            </span>
          </p>
        </div>
      )}
    </Modal>
  )
}

export default ModalNewApp
